import dayjs from '../../../../utils/dayjs';
import { OrderType } from '../../../../types';
import type { RestaurantDetailsDto } from '@kotipizzagroup/kotipizza-shops-api-client';
import { isTestMode } from '../../../../utils/isDevMode';

const PREORDER_DATE_STEP_MINUTES = 15;

// Ability to limit preorder hours.
const preorderLimit = {
  enabled: !isTestMode(),
  start: dayjs('2026-08-14T00:00:00.000Z'),
  end: dayjs('2026-08-15T22:00:00.000Z'),
  allowBeforeHour: 13,
  disableOnWeekend: true,
};

export const generatePreOrderTimesForWeek = (
  restaurants: RestaurantDetailsDto[],
  orderType: OrderType
): dayjs.Dayjs[] => {
  const date = dayjs();

  const times = restaurants.reduce((times, restaurant) => {
    if (orderType === OrderType.DELIVERY && restaurant.deliveryRedirectionRestaurant) {
      restaurant = restaurant.deliveryRedirectionRestaurant;
    }

    const intervals =
      orderType === OrderType.DELIVERY
        ? restaurant.openingHoursIntervals.delivery
        : restaurant.openingHoursIntervals.pickup;

    const restaurantTimes = intervals.reduce((all, { start, end }) => {
      const startDayjs = dayjs(start).add(
        orderType === OrderType.DELIVERY
          ? restaurant.preOrderDeliveryEstimate || 30
          : restaurant.preOrderPickupEstimate || 30,
        'minutes'
      );
      const endDayjs = dayjs(end);

      let current: dayjs.Dayjs | null = startDayjs.startOf('hour');

      const currentTimes: dayjs.Dayjs[] = [];

      while (current !== null) {
        if (!current.isValid()) {
          throw new Error('Current day for preorder sequence is invalid');
        }

        if (
          preorderLimit.enabled &&
          current.isBetween(preorderLimit.start, preorderLimit.end) &&
          current.hour() >= preorderLimit.allowBeforeHour
        ) {
          current = null;
          continue;
        }

        if (
          preorderLimit.enabled &&
          current.isBetween(preorderLimit.start, preorderLimit.end) &&
          (current.weekday() == 5 || current.weekday() == 6) &&
          preorderLimit.disableOnWeekend
        ) {
          current = null;
          continue;
        }

        if (current.isAfter(endDayjs)) {
          current = null;
          continue;
        }

        if (current.isBefore(startDayjs)) {
          current = current.add(PREORDER_DATE_STEP_MINUTES, 'minutes');
          continue;
        }

        currentTimes.push(current);
        current = current.add(PREORDER_DATE_STEP_MINUTES, 'minutes');
      }

      return [...all, ...currentTimes];
    }, [] as dayjs.Dayjs[]);

    return [...times, ...restaurantTimes];
  }, [] as dayjs.Dayjs[]);

  const validPreorderStart = date.add(2, 'hours');
  const validPreorderStartLowered = date.add(1, 'hours');

  return times
    .filter((date, index, self) => self.findIndex((d) => d.isSame(date)) === index) // Unique times only
    .filter((time) =>
      useLoweredValidPreorderTime(time)
        ? time.isAfter(validPreorderStartLowered)
        : time.isAfter(validPreorderStart)
    )
    .sort((timeA, timeB) => timeA.unix() - timeB.unix());
};

const useLoweredValidPreorderTime = (time: dayjs.Dayjs): boolean => {
  // Mon-Thu before 14:00 use lowered allowed preorder time
  return [0, 1, 2, 3].includes(time.weekday()) && time.hour() < 14;
};

export const checkRestaurantAvailableForPreorder = (
  restaurant: RestaurantDetailsDto,
  orderType: OrderType,
  preorderTime: dayjs.Dayjs
): boolean => {
  const restaurantPreorderTimes = generatePreOrderTimesForWeek([restaurant], orderType);
  return restaurantPreorderTimes.some((time) => time.isSame(preorderTime));
};
